import type { ReactNode } from 'react';
import React, { useState, useCallback, useContext, useMemo, useRef } from 'react';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import type { InviteeListOption, BulkInviteResponse } from '@atlassian/invite-people/types';
import type { DirectoryInvitePeopleSource } from '@atlassian/ptc-embeddable-directory/app-config-context/types';
import type { EnablementTouchpoint } from '@atlassian/expansion-services/services';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { CORE_INVITES_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';

import type { CoreInvitesContextType, OpenCoreInvitesFunction } from './CoreInvitesContext.tsx';
import { CoreInvitesContext } from './CoreInvitesContext.tsx';
import { CoreInvitesLoadable } from './CoreInvitesLoadable.ts';
import { CoreInvitesSource } from './constants.ts';
import { useAutoOpenInviteFromParam } from './useAutoOpenInviteFromParam.tsx';

export type CoreInvitesProviderProps = {
	children?: ReactNode;
};

const noop = (): void => {
	return undefined;
};

export function CoreInvitesProvider({ children }: CoreInvitesProviderProps): React.JSX.Element {
	const [isOpen, setIsOpen] = useState(false);
	const [initialInvitees, setInitialInvitees] = useState<InviteeListOption[]>([]);
	const [source, setSource] = useState<CoreInvitesSource | DirectoryInvitePeopleSource>(
		CoreInvitesSource.UNKNOWN,
	);
	const [showSpotlight, setShowSpotlight] = useState(false);
	const [productContext, setProductContext] = useState<Record<string, string> | undefined>(
		undefined,
	);
	const [touchpoint, setTouchpoint] = useState<EnablementTouchpoint | undefined>(undefined);
	const stablePropsRef = useRef<{
		onInviteCompleted(result?: BulkInviteResponse): void;
		onModalClose(): void;
	}>({ onInviteCompleted: noop, onModalClose: noop });

	const { useChoreographer } = useChoreographerAssets();

	const contextualInviteNudgeChoreographer = useChoreographer('cc-contextual-invite-nudge', {
		start: () => setShowSpotlight(true),
		stop: () => setShowSpotlight(false),
	});

	const { orgId } = useSessionData();

	const handleModalOpen: OpenCoreInvitesFunction = useCallback(
		(
			touchPoint,
			{ onModalClosed = noop, onInviteCompleted = noop, getContextualInviteNudgeProps } = {},
			initialInvitees = [],
			productContextMetadata = undefined,
			enablementTouchpoint = undefined,
		) => {
			setInitialInvitees(initialInvitees);
			setSource(touchPoint || CoreInvitesSource.UNKNOWN);
			setProductContext(productContextMetadata);
			setTouchpoint(enablementTouchpoint);
			stablePropsRef.current.onModalClose = onModalClosed;
			stablePropsRef.current.onInviteCompleted = onInviteCompleted;
			setIsOpen(true);

			const shouldShow = getContextualInviteNudgeProps?.()?.showSpotlight;

			if (shouldShow) {
				void contextualInviteNudgeChoreographer.startMessage();
			}
		},
		[contextualInviteNudgeChoreographer, setInitialInvitees, setSource, setIsOpen],
	);

	const handleModalClose = useCallback(() => {
		setSource(CoreInvitesSource.UNKNOWN);
		setProductContext(undefined);
		setTouchpoint(undefined);
		// remember the current value for later invocation, since we're about to override it
		const onClosed = stablePropsRef.current.onModalClose;
		stablePropsRef.current.onModalClose = noop;
		stablePropsRef.current.onInviteCompleted = noop;
		setIsOpen(false);
		onClosed();
		void contextualInviteNudgeChoreographer.stopMessage();
	}, [contextualInviteNudgeChoreographer, setSource, setIsOpen]);

	const experienceTracker = useContext(ExperienceTrackerContext);
	const failExperienceOnError = useCallback(
		(error: Error) => {
			experienceTracker.stopOnError({
				name: CORE_INVITES_EXPERIENCE,
				error,
			});
		},
		[experienceTracker],
	);

	const isInvitationsEnabled = isFeatureEnabled('confluence', 'confluence-invitations');

	const coreInviteContext: CoreInvitesContextType = useMemo(
		() => ({
			openCoreInvites: isInvitationsEnabled ? handleModalOpen : undefined,
		}),
		[handleModalOpen, isInvitationsEnabled],
	);

	const displayInfiSpotlight: boolean = true;

	const inviteCompletedHandler = useCallback(
		(result?: BulkInviteResponse) => {
			stablePropsRef.current.onInviteCompleted(result);
		},
		[stablePropsRef],
	);
	useAutoOpenInviteFromParam(isInvitationsEnabled ? handleModalOpen : noop);

	return (
		<CoreInvitesContext.Provider value={coreInviteContext}>
			{children}
			{isOpen && (
				<TransparentErrorBoundary attribution={Attribution.GROWTH} onError={failExperienceOnError}>
					<CoreInvitesLoadable
						initialInvitees={initialInvitees}
						onSendHandler={handleModalClose}
						onCancelHandler={handleModalClose}
						onInviteCompletedHandler={inviteCompletedHandler}
						source={source}
						orgId={orgId}
						displayInfiSpotlight={displayInfiSpotlight}
						showSpotlight={showSpotlight}
						experimentalProductContext={productContext}
						touchpoint={touchpoint}
					/>
				</TransparentErrorBoundary>
			)}
		</CoreInvitesContext.Provider>
	);
}
